/**
 * kitDs.ts — кит кабинета нового вида (`components/kit/ds/`, WEB-3310) и его
 * витрина `/kit` (`pages/KitDs.tsx`, только вне боя).
 *
 * Тексты компонентов эталона (`design/cabinet-rescin/kit.jsx`: LOADERR_TX и
 * подобные) переехали сюда; параметр `lang` эталона не переносится — язык
 * кабинета берётся из словаря. «Загрузка», «Отмена», «Удалить» — в `common`.
 * Строки витрины — образцы из `sections/kit-showcase.jsx` эталона.
 */
import { defineDictionary } from '../define';
import { plural } from '../plural';

export const kitDsStrings = defineDictionary(
  {
    loadErrorText:
      'The connection to the server dropped. Your data is safe — try again.',
    retry: 'Retry',
    // WEB-4003: пустое состояние таблиц и списков antd (renderKitEmpty)
    emptyNoData: 'No data',

    pageTitle: 'Kit elements',
    secButtons: 'Buttons',
    secFields: 'Fields and toggles',
    secSettings: 'Settings row',
    secMessages: 'Messages and statuses',
    secIcons: 'Icons',
    secType: 'Fonts',
    secColors: 'Colors',
    secMore: 'Tabs, bars and states',

    btnCreateFunnel: 'Create funnel',
    btnPreview: 'Preview',
    btnPublish: 'Publish',
    btnPublishing: 'Publishing…',
    btnSmall: 'Small',
    btnDuplicate: 'Duplicate',
    btnOpenApp: 'Open the app',
    btnDownloadApp: 'Download the app',

    fieldFunnelName: 'Funnel name',
    fieldFunnelNameValue: 'Meditation — onboarding',
    fieldTrialPrice: 'Trial price, $',
    fieldTrialPriceValue: '0.99',
    fieldSubdomain: 'Subdomain address',
    fieldSubdomainBad:
      'No spaces: Latin letters, digits and hyphens, for example my-funnel.',
    fieldToggles: 'Toggles',
    fieldEmailCapture: 'Email capture',
    fieldAbTest: 'A/B test',
    fieldBillingPeriod: 'Billing period',
    fieldMonth: 'Month',
    fieldYear: 'Year',

    setDomain: 'Funnel domain',
    setDomainHint: 'Where the buyer opens the funnel',
    setConnected: 'Connected',
    setEmailHint: 'Email field before the pricing screen',
    setOn: 'On',
    setDeleteProject: 'Delete project',
    setDeleteProjectHint: 'Funnels and statistics are deleted for good',

    noticeInfo: 'The funnel is published on the subdomain my-funnel.subster.ai',
    noticeOk: 'The payment system is connected',
    noticeWarn: '40 Cr left — generation will stop halfway',
    noticeTopUp: 'Top up',
    noticeError: 'Could not publish: the domain does not respond',
    noticeErrorDesc:
      'Check the CNAME record at the registrar and press “Check again”.',
    statusLive: 'Published',
    statusIdle: 'Draft',
    statusOff: 'Stopped',
    statusBad: 'Payment error',
    chipStripe: 'Stripe',
    chipApplePay: 'Apple Pay',
    badgePro: 'Pro',

    typeH1: 'Page title',
    typeH1Spec: 'Nunito Sans 700, 26',
    typeH1Sample: 'Funnels',
    typeDisplay: 'Small heading, in place',
    typeDisplaySpec: 'Comfortaa 700, 18',
    typeDisplaySample: 'Payment systems',
    typeNumber: 'Large number',
    typeNumberSpec: 'Nunito Sans 700, 30, tabular figures',
    typeBody: 'Body text',
    typeBodySpec: 'Nunito Sans 400, 15 / 1.55',
    typeBodySample:
      'A funnel is built from screens: questions, answers, a pricing screen. Changes are saved automatically.',
    typeTable: 'Table',
    typeTableSpec: 'Nunito Sans 400, 14',
    typeTableSample: 'Meditation — 7-day onboarding',
    typeTableVisits: (num: string, n: number) =>
      `${num} ${plural('en', n, { one: 'visit', other: 'visits' })}`,
    typeLabel: 'Label',
    typeLabelSpec: 'Nunito Sans 400, 13',
    typeLabelSample: 'Updated October 15 at 14:20',

    colorPage: 'Page background',
    colorPanel: 'Side menu',
    colorCard: 'Card, field',
    colorRaised: 'Dropdown, window',
    colorFg: 'Main text',
    colorFg2: 'Secondary text',
    colorFg3: 'Labels',
    colorFg4: 'Inactive',
    colorAccent: 'Button, selected',
    colorLink: 'Link, focus',
    colorWarn: 'Warning icon',
    colorErr: 'Error',
    colorSampleText: 'Sample text',
    colorSampleButton: 'Button',

    tabsAll: 'All',
    tabsLive: 'Published',
    tabsDrafts: 'Drafts',
    period: 'Period',
    period7: '7 days',
    period30: '30 days',
    period90: '90 days',
    barShare: 'Share of visits',
    barSources: 'Traffic sources',
    statVisits: 'Visits',
    statRevenue: 'Revenue',
    loadErrorTitle: 'Could not load funnels',
    moreLabel: 'Advanced',
    moreBody: 'Rare settings open in the same form.',

    infoHide: 'Hide tip',
    infoShow: 'Tip — click to expand',
    infoTip: 'Tip',

    secPop: 'Tips and lists',
    popTipHost: 'Conversion',
    popTipText: 'Share of visitors who paid, out of everyone who opened the funnel.',
    popHelp: 'Traffic is split between the variants at random, in equal shares.',
    popInfoOpen: 'Changes are saved as a draft until you publish them.',
    popInfoClosed: 'The domain starts working within an hour after it is connected.',
    popInfoAlways: 'Walk through the funnel as a visitor — answers are not counted.',
    popDomains: 'Domains',
    popCopyLink: 'Copy link',

    // списки и меню (юнит 2а-2); языки образца — словами языка кабинета
    // (кириллица в английской половине — только по перечню сторожа WEB-1874)
    popFind: 'Find…',
    fieldLanguage: 'Funnel language',
    langRu: 'Russian',
    langEn: 'English',
    langEs: 'Spanish',
    popCountries: 'Countries',
    popCountriesAll: 'All countries',
    popGroupEurope: 'Europe',
    popGroupAmericas: 'Americas',
    countryDe: 'Germany',
    countryFr: 'France',
    countryEs: 'Spain',
    countryUs: 'United States',
    countryCa: 'Canada',
    countryBr: 'Brazil',
    countryMx: 'Mexico',
    popMenuLabel: 'Actions: Meditation — onboarding',
    popMenuRename: 'Rename',
    popMenuArchive: 'Archive',
    popMenuArchiveHint: 'Unpublish the funnel first',

    // «Сбросить к стандартным» с подтверждением (ResetOpt, юнит 2б-1) — RESET_TX эталона
    resetQ: 'Reset to default?',
    resetD: 'Your selection and order will be lost.',
    resetOk: 'Reset',
    resetNo: 'Cancel',

    // окна и всплывашки на витрине (юнит 2б-1) — образцы эталона
    secWin: 'Windows and notifications',
    winOpenDel: 'Delete funnel',
    winDelTitle: 'Delete funnel?',
    winDelText: 'The funnel and its statistics will be deleted permanently. The published page will stop opening.',
    winOpenSide: 'Side panel',
    winSideTitle: 'Funnel settings',
    winSideText: 'The side panel takes the full height on the right; its body scrolls, the footer stays at the bottom.',
    winOpenLong: 'Long window',
    winLongTitle: 'Stripe events',
    winLongLine: (n: number) => `Event ${n}: payment succeeded`,
    toastOk: 'Link copied',
    toastWarn: 'Only in Pro',
    toastErr: 'Could not save changes',
    toastInfo: 'Domain check started',
    winColumns: 'Columns',
    winColName: 'Name',
    winColVisits: 'Visits',
    winResetLabel: 'Reset to default',
    winResetDone: 'Columns reset',
    winFilterAll: 'All',
    winFilterLive: 'Published',
    winSelectTwo: 'Select two',
    winClear: 'Clear selection',
    winPicked: (n: number) => `Selected: ${n}`,

    pagerPrev: 'Previous',
    pagerNext: 'Next',
    secList: 'Lists and filters',
    listPager: (a: number, b: number, n: number) => `${a}–${b} of ${n}`,
    listSearch: 'Search by name',
    listStatus: 'Status',
    listDraft: 'Drafts',
    listChannel: 'Channel',
    listReset: 'Reset',

    chartsAll: 'all',
    chartsLeftBetween: 'Left between these steps',
    chartsBefore: 'before',
    chartsThisPeriod: 'This period',
    chartsPrevPeriod: 'Previous period',

    secCharts: 'Charts',
    chartsRevenueByDay: 'Revenue by day',
    chartsNoTag: 'No tag',

    // юнит 3б: выгрузка Export (XT эталона kit-export.jsx:14–24, без prop/propTip/note/where — метка «предл.» не переносится)
    exportBtn: 'Export',
    exportFile: 'Download a file',
    exportSend: 'Send',
    exportDone: 'Done: ',
    exportWhat: 'this tab',
    exportItCsv: (w: string) => `CSV table — ${w}`,
    exportItXlsx: (w: string) => `Excel (.xlsx) — ${w}`,
    exportItJson: 'JSON — for developers',
    exportItPdf: 'PDF — for print and accounting',
    exportItSheets: 'To Google Sheets',
    exportItMail: 'Summary by email every Monday',
    exportOkCsv: (w: string) => `CSV downloaded (${w})`,
    exportOkXlsx: (w: string) => `Excel file downloaded (${w})`,
    exportOkJson: (w: string) => `JSON downloaded (${w})`,
    exportOkPdf: (w: string) => `PDF downloaded (${w})`,
    exportOkSheets: 'sheet created in Google Sheets',
    exportOkMail: 'the summary will arrive every Monday',
    // юнит 3б: витрина — таблица воронок (sections/kit-showcase.jsx:9–70) и раздел выгрузки
    secExport: 'Export',
    exportSampleWhat: 'funnel list',
    exportSampleTxLabel: 'With text that hides on a narrow screen',
    exportSampleOne: 'One format',
    exportSampleLive: 'As in production: draft off',
    exportSampleDraft: 'Draft on: all formats',
    tableFind: 'Find a funnel…',
    tableFindLabel: 'Find a funnel',
    tableStatus: 'Status',
    tableAllStatuses: 'All statuses',
    tableLive: 'Published',
    tableIdle: 'Drafts',
    tableOff: 'Stopped',
    tablePeriod: 'Period',
    table7: '7 days',
    table30: '30 days',
    table90: '90 days',
    tableNothing: 'Nothing found',
    tableResetFilters: 'Reset filters',
    tableSelectAll: 'Select all',
    tableSelectRow: (name: string) => `Select ${name}`,
    tableColName: 'Name',
    tableColStatus: 'Status',
    tableColVisits: 'Visits',
    tableColConv: 'Conversion',
    tableColRevenue: 'Revenue',
    tableColActions: 'Actions',
    tableRowActions: (name: string) => `Actions: ${name}`,
    tableDuplicate: 'Duplicate',
    tableRename: 'Rename',
    tableRow1: 'Meditation — 7-day onboarding',
    tableRow2: 'Fitness — 30-day plan',
    tableRow3: 'Languages — level test',
    tableRow4: 'Sleep — a quiz with a very long name to check how the table truncates text',
    // даты — WEB-3310 юнит 3а (эталон kit-date.jsx: DR, DT1); i — номер месяца с 0, день недели с понедельника
    monthName: (i: number) =>
      ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][i] ?? '',
    monthShort: (i: number) => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i] ?? '',
    weekday: (i: number) => ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'][i] ?? '',
    dateDays: (n: number) => `${n} ${plural('en', n, { one: 'day', other: 'days' })}`,
    datePickEnd: 'Pick the end of the period',
    dateApply: 'Apply',
    datePrev: 'Previous month',
    dateNext: 'Next month',
    datePick: 'Pick a date',
    dateClear: 'Clear',
    dateToday: 'Today',
    secDates: 'Dates',
    datesPeriod: 'Period',
    datesStart: 'Start date',
    datesEnd: 'End date',
    datesFmt: 'Date in text',
    askOpen: 'Ask AI',
    askMin: 'Collapse',
    askSend: 'Ask',
    askOpenAnalytics: 'Open in analytics',
    askQuoted: (q: string) => `“${q}”`,
    secAsk: 'AI chat',
    askSamplePh: 'Ask the data: “why did conversion drop in Germany”, “which campaign pays off”…',
    askSampleSug1: 'Why did conversion drop?',
    askSampleSug2: 'Revenue by country, 30 days',
    askSampleSug3: 'Which source is best?',
    askSampleQ1: 'How many purchases this week?',
    askSampleTitle1: 'Answer',
    askSampleLead1: 'In 7 days — ',
    askSampleStrong1: '412 purchases',
    askSampleTail1: ', most of them from the USA.',
    askSampleTitle2: 'Revenue by country, 30 days',
    askSampleLead2: 'Most of the revenue comes from the ',
    askSampleStrong2: 'USA',
    askSampleTail2: '.',
    askSampleUs: 'USA',
    askSampleDe: 'Germany',
    askSampleGb: 'United Kingdom',
  },
  {
    loadErrorText: 'Связь с сервером прервалась. Данные целы — попробуйте ещё раз.',
    retry: 'Повторить',
    emptyNoData: 'Нет данных',

    pageTitle: 'Элементы кита',
    secButtons: 'Кнопки',
    secFields: 'Поля и переключатели',
    secSettings: 'Строка настроек',
    secMessages: 'Сообщения и статусы',
    secIcons: 'Значки',
    secType: 'Шрифты',
    secColors: 'Цвета',
    secMore: 'Вкладки, полосы и состояния',

    btnCreateFunnel: 'Создать воронку',
    btnPreview: 'Предпросмотр',
    btnPublish: 'Опубликовать',
    btnPublishing: 'Публикуем…',
    btnSmall: 'Малая',
    btnDuplicate: 'Дублировать',
    btnOpenApp: 'Открыть приложение',
    btnDownloadApp: 'Скачать приложение',

    fieldFunnelName: 'Название воронки',
    fieldFunnelNameValue: 'Медитация — онбординг',
    fieldTrialPrice: 'Цена пробного периода, $',
    fieldTrialPriceValue: '0,99',
    fieldSubdomain: 'Адрес поддомена',
    fieldSubdomainBad: 'Без пробелов: латиница, цифры и дефис, например my-funnel.',
    fieldToggles: 'Переключатели',
    fieldEmailCapture: 'Сбор email',
    fieldAbTest: 'Тест A/B',
    fieldBillingPeriod: 'Период оплаты',
    fieldMonth: 'Месяц',
    fieldYear: 'Год',

    setDomain: 'Домен воронки',
    setDomainHint: 'Где покупатель откроет воронку',
    setConnected: 'Подключён',
    setEmailHint: 'Поле email перед экраном цен',
    setOn: 'Включено',
    setDeleteProject: 'Удалить проект',
    setDeleteProjectHint: 'Воронки и статистика удалятся без возврата',

    noticeInfo: 'Воронка опубликована на поддомене my-funnel.subster.ai',
    noticeOk: 'Платёжная система подключена',
    noticeWarn: 'Осталось 40 Cr — генерация остановится на середине',
    noticeTopUp: 'Пополнить',
    noticeError: 'Не удалось опубликовать: домен не отвечает',
    noticeErrorDesc:
      'Проверьте запись CNAME у регистратора и нажмите «Проверить снова».',
    statusLive: 'Опубликована',
    statusIdle: 'Черновик',
    statusOff: 'Остановлена',
    statusBad: 'Ошибка оплаты',
    chipStripe: 'Stripe',
    chipApplePay: 'Apple Pay',
    badgePro: 'Pro',

    typeH1: 'Заголовок страницы',
    typeH1Spec: 'Nunito Sans 700, 26',
    typeH1Sample: 'Воронки',
    typeDisplay: 'Небольшой заголовок, по месту',
    typeDisplaySpec: 'Comfortaa 700, 18',
    typeDisplaySample: 'Платёжные системы',
    typeNumber: 'Крупное число',
    typeNumberSpec: 'Nunito Sans 700, 30, табличные цифры',
    typeBody: 'Основной текст',
    typeBodySpec: 'Nunito Sans 400, 15 / 1,55',
    typeBodySample:
      'Воронка собирается из экранов: вопросы, ответы, экран цен. Изменения сохраняются сами.',
    typeTable: 'Таблица',
    typeTableSpec: 'Nunito Sans 400, 14',
    typeTableSample: 'Медитация — онбординг 7 дней',
    typeTableVisits: (num: string, n: number) =>
      `${num} ${plural('ru', n, { one: 'переход', few: 'перехода', many: 'переходов' })}`,
    typeLabel: 'Подпись',
    typeLabelSpec: 'Nunito Sans 400, 13',
    typeLabelSample: 'Обновлено 15 октября в 14:20',

    colorPage: 'Фон страницы',
    colorPanel: 'Боковое меню',
    colorCard: 'Карточка, поле',
    colorRaised: 'Выпадашка, окно',
    colorFg: 'Текст основной',
    colorFg2: 'Текст второй',
    colorFg3: 'Подписи',
    colorFg4: 'Неактивное',
    colorAccent: 'Кнопка, выбранное',
    colorLink: 'Ссылка, фокус',
    colorWarn: 'Значок предупреждения',
    colorErr: 'Ошибка',
    colorSampleText: 'Текст образца',
    colorSampleButton: 'Кнопка',

    tabsAll: 'Все',
    tabsLive: 'Опубликованные',
    tabsDrafts: 'Черновики',
    period: 'Период',
    period7: '7 дней',
    period30: '30 дней',
    period90: '90 дней',
    barShare: 'Доля переходов',
    barSources: 'Источники переходов',
    statVisits: 'Переходы',
    statRevenue: 'Выручка',
    loadErrorTitle: 'Не удалось загрузить воронки',
    moreLabel: 'Дополнительно',
    moreBody: 'Редкие настройки раскрываются в той же форме.',

    infoHide: 'Скрыть подсказку',
    infoShow: 'Подсказка — нажмите, чтобы развернуть',
    infoTip: 'Подсказка',

    secPop: 'Подсказки и списки',
    popTipHost: 'Конверсия',
    popTipText: 'Доля оплативших среди всех, кто открыл воронку.',
    popHelp: 'Трафик делится между вариантами случайно и поровну.',
    popInfoOpen: 'Изменения хранятся черновиком, пока вы их не опубликуете.',
    popInfoClosed: 'Домен начинает работать в течение часа после подключения.',
    popInfoAlways: 'Пройдите воронку как посетитель — ответы не засчитываются.',
    popDomains: 'Домены',
    popCopyLink: 'Скопировать ссылку',

    popFind: 'Найти…',
    fieldLanguage: 'Язык воронки',
    // WEB-4117: языки — самоназваниями, как у витрины эталона (kit-showcase.jsx:112).
    langRu: 'Русский',
    langEn: 'English',
    langEs: 'Español',
    popCountries: 'Страны',
    popCountriesAll: 'Все страны',
    popGroupEurope: 'Европа',
    popGroupAmericas: 'Америка',
    countryDe: 'Германия',
    countryFr: 'Франция',
    countryEs: 'Испания',
    countryUs: 'США',
    countryCa: 'Канада',
    countryBr: 'Бразилия',
    countryMx: 'Мексика',
    popMenuLabel: 'Действия: Медитация — онбординг',
    popMenuRename: 'Переименовать',
    popMenuArchive: 'В архив',
    popMenuArchiveHint: 'Сначала снимите воронку с публикации',

    resetQ: 'Сбросить к стандартным?',
    resetD: 'Ваш выбор и порядок пропадут.',
    resetOk: 'Сбросить',
    resetNo: 'Отмена',

    secWin: 'Окна и уведомления',
    winOpenDel: 'Удалить воронку',
    winDelTitle: 'Удалить воронку?',
    winDelText: 'Воронка и её статистика удалятся без возврата. Опубликованная страница перестанет открываться.',
    winOpenSide: 'Боковая панель',
    winSideTitle: 'Настройки воронки',
    winSideText: 'Боковая панель — справа во всю высоту; тело прокручивается, подвал прижат вниз.',
    winOpenLong: 'Длинное окно',
    winLongTitle: 'События Stripe',
    winLongLine: (n: number) => `Событие ${n}: оплата прошла`,
    toastOk: 'Ссылка скопирована',
    toastWarn: 'Только в Pro',
    toastErr: 'Не удалось сохранить изменения',
    toastInfo: 'Проверка домена запущена',
    winColumns: 'Колонки',
    winColName: 'Название',
    winColVisits: 'Переходы',
    winResetLabel: 'Сбросить к стандартным',
    winResetDone: 'Колонки сброшены',
    winFilterAll: 'Все',
    winFilterLive: 'Опубликованные',
    winSelectTwo: 'Выбрать две',
    winClear: 'Снять выбор',
    winPicked: (n: number) => `Выбрано: ${n}`,

    pagerPrev: 'Назад',
    pagerNext: 'Вперёд',
    secList: 'Списки и фильтры',
    listPager: (a: number, b: number, n: number) => `${a}–${b} из ${n}`,
    listSearch: 'Поиск по названию',
    listStatus: 'Статус',
    listDraft: 'Черновики',
    listChannel: 'Канал',
    listReset: 'Сбросить',

    chartsAll: 'все',
    chartsLeftBetween: 'Ушли между этими шагами',
    chartsBefore: 'было',
    chartsThisPeriod: 'Этот период',
    chartsPrevPeriod: 'Прошлый период',

    secCharts: 'Графики',
    chartsRevenueByDay: 'Выручка по дням',
    chartsNoTag: 'Без метки',

    // юнит 3б: выгрузка Export (XT эталона kit-export.jsx:14–24)
    exportBtn: 'Выгрузить',
    exportFile: 'Скачать файлом',
    exportSend: 'Отправить',
    exportDone: 'Готово: ',
    exportWhat: 'эта вкладка',
    exportItCsv: (w: string) => `Таблица CSV — ${w}`,
    exportItXlsx: (w: string) => `Excel (.xlsx) — ${w}`,
    exportItJson: 'JSON — для разработчиков',
    exportItPdf: 'PDF — для печати и бухгалтерии',
    exportItSheets: 'В Google Таблицы',
    exportItMail: 'Сводка на почту по понедельникам',
    exportOkCsv: (w: string) => `CSV скачан (${w})`,
    exportOkXlsx: (w: string) => `файл Excel скачан (${w})`,
    exportOkJson: (w: string) => `JSON скачан (${w})`,
    exportOkPdf: (w: string) => `PDF скачан (${w})`,
    exportOkSheets: 'таблица создана в Google Таблицах',
    exportOkMail: 'сводка будет приходить по понедельникам',
    // юнит 3б: витрина — таблица воронок и раздел выгрузки
    secExport: 'Выгрузка',
    exportSampleWhat: 'список воронок',
    exportSampleTxLabel: 'С подписью, которая прячется на узком экране',
    exportSampleOne: 'Один формат',
    exportSampleLive: 'Как на бою: черновик выключен',
    exportSampleDraft: 'Черновик включён: все форматы',
    tableFind: 'Найти воронку…',
    tableFindLabel: 'Найти воронку',
    tableStatus: 'Статус',
    tableAllStatuses: 'Все статусы',
    tableLive: 'Опубликованные',
    tableIdle: 'Черновики',
    tableOff: 'Остановленные',
    tablePeriod: 'Период',
    table7: '7 дней',
    table30: '30 дней',
    table90: '90 дней',
    tableNothing: 'Ничего не нашлось',
    tableResetFilters: 'Сбросить фильтры',
    tableSelectAll: 'Выбрать все',
    tableSelectRow: (name: string) => `Выбрать ${name}`,
    tableColName: 'Название',
    tableColStatus: 'Статус',
    tableColVisits: 'Переходы',
    tableColConv: 'Конверсия',
    tableColRevenue: 'Выручка',
    tableColActions: 'Действия',
    tableRowActions: (name: string) => `Действия: ${name}`,
    tableDuplicate: 'Дублировать',
    tableRename: 'Переименовать',
    tableRow1: 'Медитация — онбординг 7 дней',
    tableRow2: 'Фитнес — план на 30 дней',
    tableRow3: 'Языки — тест уровня',
    tableRow4: 'Сон — квиз с очень длинным названием, чтобы проверить, как таблица обрезает текст',
    monthName: (i: number) =>
      ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'][i] ?? '',
    monthShort: (i: number) =>
      ['янв.', 'февр.', 'марта', 'апр.', 'мая', 'июня', 'июля', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'][i] ?? '',
    weekday: (i: number) => ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'][i] ?? '',
    dateDays: (n: number) => `${n} ${plural('ru', n, { one: 'день', few: 'дня', many: 'дней' })}`,
    datePickEnd: 'Выберите конец периода',
    dateApply: 'Применить',
    datePrev: 'Предыдущий месяц',
    dateNext: 'Следующий месяц',
    datePick: 'Выберите дату',
    dateClear: 'Очистить',
    dateToday: 'Сегодня',
    secDates: 'Даты',
    datesPeriod: 'Период',
    datesStart: 'Дата начала',
    datesEnd: 'Дата окончания',
    datesFmt: 'Дата текстом',
    askOpen: 'Спросить ИИ',
    askMin: 'Свернуть',
    askSend: 'Спросить',
    askOpenAnalytics: 'Открыть в аналитике',
    askQuoted: (q: string) => `«${q}»`,
    secAsk: 'Чат с ИИ',
    askSamplePh: 'Спросите данные: «почему упала конверсия в Германии», «какая кампания окупается»…',
    askSampleSug1: 'Почему упала конверсия?',
    askSampleSug2: 'Выручка по странам за 30 дней',
    askSampleSug3: 'Какой источник лучше?',
    askSampleQ1: 'Сколько покупок за неделю?',
    askSampleTitle1: 'Ответ',
    askSampleLead1: 'За 7 дней — ',
    askSampleStrong1: '412 покупок',
    askSampleTail1: ', больше всего из США.',
    askSampleTitle2: 'Выручка по странам за 30 дней',
    askSampleLead2: 'Больше всего выручки приносят ',
    askSampleStrong2: 'США',
    askSampleTail2: '.',
    askSampleUs: 'США',
    askSampleDe: 'Германия',
    askSampleGb: 'Великобритания',
  },
);
